import api from "./api";

export interface ProfileFieldDiff {
  field: string;
  label: string;
  previous: any;
  updated: any;
}

export interface ProfileAuditLog {
  id: string;
  action: string;
  title: string;
  actorName?: string;
  actorRole?: string;
  entity?: string;
  entityId?: string;
  targetName?: string;
  metadata?: ProfileFieldDiff[] | null;
  createdAt: string;
  status?: boolean;
}

export interface ProfileData {
  id: number;
  name: string;
  email: string;
  role: string;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;

  // Doctor-specific fields
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  medicalRegistrationNumber?: string | null;
  licenseNumber?: string | null;
  medicalLicenseNumber?: string | null;
  specialization?: string | null;
  hospitalId?: number | null;
  hospitalName?: string | null;
  department?: string | null;
  qualification?: string | null;
  yearsOfExperience?: number | null;
  onboardingStatus?: "not started" | "in progress" | "completed" | string;

  // Admin-specific fields
  accessLevel?: string;
  experience?: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  confirmPassword?: string;
}

const unwrap = <T,>(result: T | { data: T }): T =>
  result && typeof result === "object" && "data" in result
    ? (result as { data: T }).data
    : (result as T);

export const getProfile = async (): Promise<ProfileData> => {
  try {
    const response = await api.get("/profile/me");
    return unwrap<ProfileData>(response.data);
  } catch (err: any) {
    if (err?.response?.status === 404) {
      const response = await api.get("/auth/profile");
      return unwrap<ProfileData>(response.data);
    }
    throw err;
  }
};

export const updateName = async (name: string): Promise<ProfileData> => {
  try {
    const response = await api.put("/profile/name", { name });
    return unwrap<ProfileData>(response.data);
  } catch (err: any) {
    if (err?.response?.status === 404) {
      const response = await api.put("/auth/profile/name", { name });
      return unwrap<ProfileData>(response.data);
    }
    throw err;
  }
};

export const requestEmailChange = async (
  newEmail: string
): Promise<{ message: string; otp?: string }> => {
  const response = await api.post("/profile/email/change-request", { newEmail });
  return unwrap<{ message: string; otp?: string }>(response.data);
};

export const verifyEmailChange = async (otp: string): Promise<ProfileData> => {
  const response = await api.post("/profile/email/verify", { otp });
  return unwrap<ProfileData>(response.data);
};

export const updateDepartment = async (
  department: string
): Promise<ProfileData> => {
  const response = await api.put("/profile/department", {
    department: department.trim(),
  });
  return unwrap<ProfileData>(response.data);
};

export const updateMedicalLicenseNumber = async (
  medicalLicenseNumber: string
): Promise<ProfileData> => {
  const response = await api.put("/profile/medical-license", {
    medicalLicenseNumber: medicalLicenseNumber.trim(),
  });
  return unwrap<ProfileData>(response.data);
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string,
  confirmPassword: string
): Promise<{ message: string }> => {
  const payload: ChangePasswordPayload = {
    currentPassword,
    newPassword,
    confirmPassword,
  };
  const response = await api.put("/profile/password", payload);
  return unwrap<{ message: string }>(response.data);
};

export const getProfileAuditLogs = async (): Promise<ProfileAuditLog[]> => {
  try {
    const response = await api.get("/profile/audit-log");
    const items = unwrap<ProfileAuditLog[] | unknown>(response.data);
    return Array.isArray(items) ? (items as ProfileAuditLog[]) : [];
  } catch (err: any) {
    console.error("Failed to fetch profile audit logs:", err);
    return [];
  }
};
